import { ProductLineAttr, RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { Catalog, EngValue, PlanAttributeType } from 'types/productServiceV2';

/**
 * Default Catalog object
 */
export const INITIAL_CATALOG: Catalog = Object.freeze({
  id: '',
  rateSegment: 'GROUP',
  productLineAttributes: [],
  marketingTags: [],
});

/**
 * Retrieves product line attributes from a given catalog based on the provided product line ID.
 *
 * @param catalog - The catalog from which to retrieve product line attributes.
 * @param productLineId - The ID of the product line for which to retrieve attributes.
 * @param filteringType - Optional flag indicating whether to filter attributes based on rate segment
 * @returns An array of product line attributes that match the provided product line ID.
 *          If `filteringType` is 'CUSTOM', the returned array will exclude attributes of type 'link'
 *          and those with a category of 'Policy Attributes'.
 */
function getProductLineAttributesFromCatalog(
  catalog: Catalog,
  productLineId: ProductLineId,
  rateSegment: RateSegment = 'GROUP',
): ProductLineAttr[] {
  const label = PRODUCT_LINES_CONTENT_MAP[productLineId].joinedLabel;
  const filteredByPLine = catalog.productLineAttributes.filter((attribute) => {
    return attribute.productLine.replaceAll(' ', '') === label;
  });

  if (rateSegment === 'GROUP') {
    return filteredByPLine;
  }

  if (rateSegment === 'CUSTOM') {
    return filteredByPLine.filter((attribute) => {
      return !attribute?.link && attribute.category.toLowerCase() !== 'policy attributes';
    });
  }
  return [];
}

/**
 * Determines the status of a plan attribute based on the provided English values.
 *
 * @param engValues - An array of values associated with the plan attribute.
 * @returns A string representing the status of the plan attribute:
 *          - 'UNFILLED' if `engValues` is not provided.
 *          - 'FILLED_UNCOVERED' if `engValues` is an empty array.
 *          - 'FILLED_COVERED' if `engValues` is not empty.
 */
function getPlanAttributeStatus(engValues: EngValue[] | undefined): PlanAttributeType {
  if (!engValues) {
    return 'UNFILLED';
  }
  if (engValues.length === 0) {
    return 'FILLED_UNCOVERED';
  }
  return 'FILLED_COVERED';
}

export { getProductLineAttributesFromCatalog, getPlanAttributeStatus };
